import { LoadingTypography } from '@modules/common/components/LoadingTypography';
import { EqlFieldName } from '@modules/common/constants';
import { useFetchEqlFieldValues } from '@modules/common/hooks';
import {
  useFetchMeasuredAsCriteria,
  useGetSelectedGroupByOption,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { VisualisationType } from '@modules/quantify/store/types';
import { Box, CardContent } from '@mui/material';
import { useTheme } from '@mui/system';
import { EqlFieldValueType, ObjectType } from 'generated/graphql';
import { FieldNames, SentimentName } from 'v2/core/entities/fields';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { trackQuantifyTemplatesEvent } from './utils';

export function TopReasonsForNegativeFeedbackTemplate() {
  const theme = useTheme();
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const {
    isLoading: isLoadingEqlFields,
    selectedGroupByOption: reasonGroupBy,
  } = useGetSelectedGroupByOption(FieldNames.Reason);
  const { criteriaList } = useFetchMeasuredAsCriteria({
    field: reasonGroupBy,
    fetchPolicy: 'cache-first',
  });

  const [, { values: reviewSentimentOptions, isLoading: isLoadingEqlValues }] =
    useFetchEqlFieldValues({
      eqlField: EqlFieldName.UserSentiment,
    });

  const onClick = () => {
    const negativeSentiment = reviewSentimentOptions.find(
      (reviewSentimentOption) => {
        return (
          reviewSentimentOption.displayName.toLowerCase() ===
          SentimentName.NEGATIVE.toLowerCase()
        );
      },
    );

    const measuredAsCriteria =
      criteriaList.find((criteria) => {
        return (
          criteria.objectType === ObjectType.Feedback &&
          criteria.valueType === EqlFieldValueType.None
        );
      }) ?? null;

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        groupBy: reasonGroupBy,
        measuredAsCriteria,
        whereClauseConfig: negativeSentiment
          ? {
              whereClause: {
                filter: {
                  type: OperationType.AND,
                  reviewSentimentFilter: {
                    sentimentIDs: [String(negativeSentiment.value)],
                  },
                },
              },
            }
          : undefined,
      },
    });

    dispatch({
      type: QuantifyActionType.SET_METADATA,
      payload: {
        activeVisualisationType: VisualisationType.BAR,
      },
    });

    trackQuantifyTemplatesEvent({
      eventName:
        'Quantify Templates - "Top Reasons for negative feedback" clicked',
    });
  };

  const isLoading = isLoadingEqlFields || isLoadingEqlValues;

  return (
    <CardContent
      onClick={onClick}
      sx={{
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      <LoadingTypography variant="subtitle2" isLoading={isLoading}>
        Top{' '}
        <Box component="span" color={theme.palette.secondary.main}>
          Reasons
        </Box>{' '}
        for Negative Feedback
      </LoadingTypography>
    </CardContent>
  );
}
